import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  signal,
  viewChild,
} from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { Popover, PopoverModule } from 'primeng/popover';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { CloseOverlayOnNavigationDirective } from '@tourfold/libraries/shared-ui';
import type { CommentMention, MentionTargetDetail } from '@tourfold/libraries/tf-api-client-public-v2';
import { CommentsService } from '../services/comments.service';

/**
 * Read-path chip rendered inline inside the markdown body for every `@[name](type:uuid)` token.
 *
 * **Visual states:**
 *  - Live mention → primary-tinted chip, click opens a detail popover (email + roles).
 *  - Deleted mention (`mention.isDeleted === true`) → greyed-out chip; click still opens the
 *    popover but immediately shows the "deleted target" tombstone view without fetching detail.
 *
 * Detail is **lazy-loaded** on the first popover open per chip instance: a tap on the network
 * happens only when the user actually clicks. Subsequent opens reuse the cached result.
 */
@Component({
  selector: 'tf-mention-chip',
  standalone: true,
  imports: [TranslateModule, PopoverModule, ProgressSpinnerModule, CloseOverlayOnNavigationDirective],
  templateUrl: './mention-chip.component.html',
  styleUrl: './mention-chip.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MentionChipComponent {
  private readonly commentsService = inject(CommentsService);

  /** The structured mention attached to the parent comment's `mentions[]` array. */
  readonly mention = input.required<CommentMention>();

  protected readonly popover = viewChild.required<Popover>('op');

  /** Lazy-loaded detail. `null` until the popover is opened, then either a detail or an error. */
  protected readonly detail = signal<MentionTargetDetail | null>(null);
  protected readonly loading = signal(false);
  protected readonly errored = signal(false);

  protected readonly displayLabel = computed<string>(() => {
    const live = this.detail()?.displayName;
    return live && live.length > 0 ? live : this.mention().displayName;
  });

  protected onChipClick(event: Event): void {
    this.popover().toggle(event);
  }

  protected onPopoverShow(): void {
    if (this.mention().isDeleted) {
      // No fetch — the cached display name + isDeleted flag is everything the popover needs.
      return;
    }
    if (this.detail() !== null || this.loading()) return;
    this.loading.set(true);
    this.errored.set(false);
    this.commentsService
      .getMentionDetail({ type: this.mention().type, id: this.mention().id })
      .then((detail) => {
        this.detail.set(detail);
      })
      .catch(() => {
        // 404 / network error — treat as deleted so the popover still says something useful
        // without crashing. The chip itself keeps its `isDeleted=false` state because that's
        // what the response says about the mention at render time; the popover just falls back
        // to the cached label.
        this.errored.set(true);
      })
      .finally(() => {
        this.loading.set(false);
      });
  }
}
